// Split out of src/lib/utils.js (docs/FILE_SPLIT_RUN1_SPEC.md, move-only). utils.js re-exports
// everything here, so existing imports keep working; new code may import from here directly.

import { AVATARS, DEFAULT_COACH_TEST_SIZE, DEFAULT_PASS_THRESHOLD, DEFAULT_SPENDING_CAP_GBP, VOCAB_TEST_SIZE, genPasscode, nowIso, uid } from "./constants.js";
import { DEFAULT_VAULT_INTEREST_APR, clampApr } from "./vaultInterest.js";
import { tokensToPence } from "./money.js";
import { browserTimezone, todayStr } from "./dates.js";

export function defaultState() {
  const tz = browserTimezone();
  return {
    // pinConfigured: false means the household is still on the "123456" placeholder and
    // hasn't had a real PIN set yet — Parent Mode stays open with no PIN prompt at all until
    // the wizard's PinSetupStep (or Settings) sets one, flipping this to true for good.
    // householdPasscode: generated once, right here, at creation time — a device joining
    // this household later needs it alongside the code. Like parentPin, the server strips
    // it from every plain GET; the client only ever sees it again through the PIN-gated
    // reveal-passcode call in Settings.
    // LB-13 (W2.6): weekly streak reward mode — "auto" credits the tokens the moment the
    // week's threshold is crossed (through the spending-cap check, like Learn & Earn);
    // "manual" raises a request for a parent to tap. New households: auto (principle 1);
    // pre-existing households are backfilled to manual by migrateState (principle 3).
    // timezone (LB-316): the creating device's IANA zone, so the worker can send a
    // household's lifecycle email at 17:xx where the family lives rather than 17:xx UTC.
    // Omitted entirely when the browser won't say — absent means Europe/London, and an
    // absent field is honest about not knowing in a way "Europe/London" would not be.
    settings: { pinConfigured: false, householdPasscode: genPasscode(), exchangeRate: 10, defaultHabitThreshold: 7, defaultHabitReward: 20, applyHabitToAll: true, capMode: "global", habitRewardMode: "auto", doubleTokenDay: true, taskReminderHour: 18, referralSharedAt: null, ...(tz ? { timezone: tz } : {}) },
    ownerEmail: null,
    // LB-337: the owner's first name, captured beside the email on the create form and
    // editable in Settings -> Household. Deliberately a plain sibling of ownerEmail rather
    // than a per-device or per-parent shape: LB-312 turns {ownerEmail, ownerName} into the
    // Owner row of its adults list in ONE migration, which is the shape that ticket asked
    // for. null (not "") so "never captured" is distinguishable from "cleared", which is
    // what the one-time prompt for existing households will key off when it is built.
    ownerName: null,
    // LB-339: the household's adults. Empty at creation and filled by the WORKER on its
    // first read (worker/lib/adults.js ensureAdults), never here — the Owner row needs a
    // PIN in it, and this side must never invent one for the same reason migrateState has
    // never defaulted parentPin: the server strips every pin from the GET, so anything
    // invented here would be sent back on the next save as a PIN change. What the client
    // receives is the list minus the secrets; read it through src/lib/adults.js only.
    adults: [],
    // Owner-email verification (review 2.1): false for every genuinely new household —
    // the welcome email (which carries the household code) is held until this flips true
    // via the emailed verify-owner link. See migrateState() for why every household that
    // predates this feature grandfathers to true instead.
    ownerEmailVerified: false,
    profiles: [],
    transactions: [],
    // Empty on purpose since Aug 2026 (SPENDING_GUARDRAILS_SCOPE.md). The starter reading
    // pair used to be seeded here as household-GLOBAL activities at hardcoded 2/5 tokens —
    // values tuned for an exchange rate of 10 and blind to any spending cap. They are now
    // seeded PER CHILD by seedStarterTasks(), priced from that child's own cap, so a child
    // still never starts with nothing to earn from (which is what keeps the wizard's
    // skip-all interstitial condition unreachable) but the values actually mean something.
    activities: [],
    habits: [],
    habitLog: [],
    requests: [],
    // Parent-defined, non-monetary redemptions (screen time, choose dinner, etc.) a kid can
    // claim with tokens — same global/child-scoped shape as `activities`, just token-priced
    // spends instead of token-earning taps.
    experienceRewards: [],
    learningContent: {},
    // Drives the guided setup wizard shown right after a household is created: which step
    // the parent is on (1 Children, 2 PIN, 3 Earning & Habits, 4 Learn & Earn) and whether
    // they've finished or skipped through to the end. Households created before this existed
    // never have these fields — migrateState() backfills onboardingDone: true for those so
    // the wizard doesn't retroactively appear for existing families.
    onboardingStep: 1,
    onboardingDone: false,
    // Custom spelling/vocab words waiting to be flushed to the shared word bank
    // (TTS_SCOPE.md §5.3.1) — plain strings, queued here rather than sent immediately so
    // submission timing is decorrelated from the moment a parent types something. Flushed
    // and cleared by App.jsx on a LATER app open, never on the same action that added the
    // word. Household data structurally, but transient: nothing reads this except the
    // flush effect, and it carries no household identifier when it's sent.
    pendingWordbankSubmissions: [],
    // 1 once this household's existing words have been queued for the word bank
    // (wordbankBackfillWords, run once from migrateState). A new household has none to catch up.
    wordbankBackfill: 1,
    _meta: { revision: 0, lastModified: null },
  };
}

// Every word this household would send to the word bank today: its custom spelling words, its
// custom vocabulary words, and the words of its homework SPELLING lists (the three places that
// queue a word as it is added). Bare strings, trimmed, once each.
export function wordbankBackfillWords(state) {
  const out = []; const seen = new Set();
  const add = (w) => { const t = String(w || "").trim(); if (t && !seen.has(t)) { seen.add(t); out.push(t); } };
  for (const lc of Object.values((state && state.learningContent) || {})) {
    if (!lc || typeof lc !== "object") continue;
    for (const w of Array.isArray(lc.spellingWords) ? lc.spellingWords : []) add(w && w.word);
    for (const v of Array.isArray(lc.vocabBank) ? lc.vocabBank : []) if (v && v.source === "custom") add(v.word);
    const lists = lc.homework && Array.isArray(lc.homework.lists) ? lc.homework.lists : [];
    for (const l of lists) if (l && l.type === "spelling") for (const p of l.pairs || []) add(p && p.a);
  }
  return out;
}

// Fills in any fields missing from older saved data (e.g. households created
// before Requests/Learn & Earn existed) so loading old data never crashes.

export function migrateState(parsed) {
  if (!parsed.settings) parsed.settings = {};
  // Deliberately NOT defaulting parentPin here: the server never sends the real PIN back
  // on a GET (it's stripped for security), so its absence here just means "this device
  // doesn't know it" — not "the household's PIN is 123456". Inventing a placeholder value
  // would get sent back on every future save and rejected by the server as a bogus PIN
  // change attempt, silently blocking that save. Leaving it undefined lets JSON.stringify
  // omit the field entirely, so the server knows to leave the real stored PIN untouched.
  // householdPasscode is deliberately NOT defaulted here either, for the exact same reason
  // — the server strips it from every GET too, and a device only ever learns the real value
  // through the PIN-gated reveal-passcode call (see SettingsTab), never through migration.
  if (parsed.ownerEmail === undefined) parsed.ownerEmail = null;
  // LB-337: no grandfathering the way ownerEmailVerified does — a household that predates
  // the field genuinely has no name on file, and null is what says so. Every surface that
  // reads it falls back rather than nagging (principle 5), and Settings -> Household is the
  // route for an existing owner to fill it in.
  if (parsed.ownerName === undefined) parsed.ownerName = null;
  // LB-339: the shape only. An ARRAY so every reader can iterate without guarding, but
  // deliberately never populated here — the worker owns this migration because the Owner
  // row carries a PIN and this side must never invent one (same trap as parentPin, above:
  // a value invented client-side is sent back on the next save). An empty list here for a
  // household that has one on the server is harmless: the GET that follows brings the real
  // one, and the PUT merge preserves it when the client omits the pins.
  if (!Array.isArray(parsed.adults)) parsed.adults = [];
  // Grandfathered true (review 2.1): every household saved before this field existed
  // already received its welcome email the old way — treating them as unverified would
  // nag a settled state. Only genuinely new households (defaultState() above) start false.
  if (parsed.ownerEmailVerified === undefined) parsed.ownerEmailVerified = true;
  // W2-PR(iii) — settings audit log (review 3.6): a small ring of "who changed what,
  // when" for the settings that move money or security. Client-written (the device
  // token authenticates the writer; the deviceId stamp identifies it), capped so it
  // can never bloat the household document.
  if (!Array.isArray(parsed.settingsLog)) parsed.settingsLog = [];
  // P3 hard cap (27 Aug): clamp any stored APR that predates the 25% ceiling, so the
  // stored number matches the rate clampApr() actually pays.
  if (parsed.settings.vaultInterest && typeof parsed.settings.vaultInterest.apr === "number") {
    parsed.settings.vaultInterest.apr = clampApr(parsed.settings.vaultInterest.apr);
  }
  if (parsed.settings.exchangeRate === undefined) parsed.settings.exchangeRate = 10;
  // timezone (LB-316): normalise, never invent. A household saved before this field
  // existed keeps no timezone and reads as Europe/London in the worker; backfilling it
  // from whichever device loaded the state next would silently move that household's
  // email hour on the say-so of a device that may not be the parent's.
  if (parsed.settings.timezone !== undefined) {
    const tz = typeof parsed.settings.timezone === "string" ? parsed.settings.timezone.trim() : "";
    if (tz) parsed.settings.timezone = tz;
    else delete parsed.settings.timezone;
  }
  // showGBP deleted (Alan, Aug 2026). £ is the Savings Vault's unit of currency, so a switch
  // that hid £ values while the vault reported a £ balance was incoherent. It also never did
  // what its label claimed: it gated exactly ONE display (a goal's £ target) while the vault
  // balance, Total Wealth strip and interest line all rendered £ regardless. Its defaults had
  // diverged too — true for new households, false for migrated ones — so deleting it also
  // resolves a split-brain nobody chose.
  delete parsed.settings.showGBP;
  // Households saved before pinConfigured existed already have a real PIN set (they've been
  // using the app), so default them to "configured" rather than reopening Parent Mode with
  // no PIN for an existing family.
  if (parsed.settings.pinConfigured === undefined) parsed.settings.pinConfigured = true;
  // "Daily habits & streaks" settings — settings.defaultHabitThreshold/defaultHabitReward hold
  // the shared values shown/edited in AdjustTab when applyHabitToAll is true (the default);
  // when false, each child's own habitThreshold/habitReward (below) are edited independently
  // instead. See AdjustTab's toggleApplyHabitToAll for exactly what happens on flipping this.
  if (parsed.settings.defaultHabitThreshold === undefined) parsed.settings.defaultHabitThreshold = 7;
  if (parsed.settings.defaultHabitReward === undefined) parsed.settings.defaultHabitReward = 20;
  if (parsed.settings.applyHabitToAll === undefined) parsed.settings.applyHabitToAll = true;
  // LB-13: households that predate the switch keep the old behaviour (every weekly reward
  // waits for a parent tap) until a parent flips it in Daily habits.
  if (parsed.settings.habitRewardMode !== "auto" && parsed.settings.habitRewardMode !== "manual") parsed.settings.habitRewardMode = "manual";
  // LB-248: double token day is on by default for every household (opt-out in Settings → Money).
  if (parsed.settings.doubleTokenDay === undefined) parsed.settings.doubleTokenDay = true;
  // LB-265 (Alan, 12 Sep): the evening "finish your tasks" nudge and the 7am "tasks are
  // ready" ping became ONE choice — settings.taskReminderHour, an hour (7 or 16–20) or null
  // for off. Both old settings defaulted on, so a child with unfinished tasks was buzzed
  // twice a day about the same list.
  //
  // Which one survives: the EVENING hour, because it is the one a parent actually chose from
  // a list, and it is the one that protects a streak. Morning survives only where the evening
  // reminder had been explicitly switched off — that parent said no to the evening nudge and
  // yes to the silent morning one, and honouring that is the whole point of migrating rather
  // than resetting. A household that had turned both off stays off.
  //
  // The two old keys are read once here and then left alone rather than deleted: a rollback
  // would otherwise land on households whose preferences had been erased.
  if (parsed.settings.taskReminderHour === undefined) {
    const eveningOff = parsed.settings.dailyReminderHour === null;
    const morningOn = parsed.settings.morningReminderOn !== false;
    if (!eveningOff) parsed.settings.taskReminderHour = Number.isInteger(parsed.settings.dailyReminderHour) ? parsed.settings.dailyReminderHour : 18;
    else parsed.settings.taskReminderHour = morningOn ? 7 : null;
  }
  // LB-19 Learning Coach: OFF by default for every household, new and existing (Alan,
  // 12 Sep). The switch and the advert are visible to everyone — this is a trial parents opt
  // into, not a dark launch — but nobody is enrolled without asking. Same precedent as Vault
  // Interest: a feature that changes what a child sees waits for a parent to say yes.
  // Dark launch, 13 Sep (Alan): ship with Learning Coach off, prove it on his own household,
  // then turn everyone on. The key is deliberately NOT written here. Writing false into every
  // household on first load would make that last step unsafe: a stored false cannot be told
  // apart from a parent who deliberately switched it off, so "enable everyone" would have to
  // override both. All 21 reads test for === true, so unset already behaves as off without
  // recording a decision nobody made. Turning everyone on is then a one-line default change
  // in a release rather than a job that rewrites every household's saved state.
  // Vault Interest ("Money Monday" — docs/VAULT_INTEREST_SCOPE.md): household-level yearly
  // rate, paid weekly. Off by default for every existing and new household — a parent
  // explicitly opts in from Settings → Rewards & Currency, which is also the moment the
  // per-child anchors get set (see VaultInterestCard) so nothing is ever paid retroactively.
  if (!parsed.settings.vaultInterest || typeof parsed.settings.vaultInterest !== "object") parsed.settings.vaultInterest = { enabled: false, apr: DEFAULT_VAULT_INTEREST_APR };
  if (typeof parsed.settings.vaultInterest.enabled !== "boolean") parsed.settings.vaultInterest.enabled = false;
  parsed.settings.vaultInterest.apr = clampApr(parsed.settings.vaultInterest.apr);
  if (!Array.isArray(parsed.profiles)) parsed.profiles = [];
  // Households saved before the setup wizard existed have neither field. A household that
  // already has children has clearly been used before — treat it as already done so the
  // wizard doesn't intrude on an existing family. But a household with zero children never
  // really finished setting up (whether it's brand new or was abandoned early on), so it's
  // worth guiding them through the wizard now — starting at step 1 (Children). Step 2 (PIN)
  // gets skipped automatically for these since pinConfigured is already true above.
  if (parsed.onboardingDone === undefined) parsed.onboardingDone = parsed.profiles.length > 0;
  if (parsed.onboardingStep === undefined) parsed.onboardingStep = 1;
  // PIN before Notifications (Alan, 20 Sep). The PIN used to be step 2, straight after
  // Children; it now comes after Learn & Earn. A household saved on the OLD step 2 without a
  // PIN has not seen Token Setup yet, so it moves on to Token Setup and meets the PIN at its
  // new place. A household on the NEW step 2 has already been through the fork, which
  // always records the route and _wizardPastTokens, so it is left exactly where it is. This
  // runs on every load; the two markers are what make it a one-off.
  if (!parsed.onboardingDone && parsed.onboardingStep === 2 && parsed.settings?.pinConfigured !== true
      && !parsed.onboardingRoute && !parsed.settings?._wizardPastTokens) {
    parsed.onboardingStep = 3;
  }
  // LB-355: goals are stored as money. A goal saved as a token target is converted ONCE, at
  // the rate in force when this runs, so its price in pounds is what it was worth the moment
  // before; targetTokens is then dropped so there is one price, not two that can disagree.
  // Idempotent: a goal that already has targetPence is left alone.
  const goalRate = parsed.settings.exchangeRate > 0 ? parsed.settings.exchangeRate : 10;
  parsed.profiles.forEach((p) => {
    if (!Array.isArray(p.goals)) p.goals = [];
    p.goals.forEach((g) => {
      if (!g || typeof g !== "object") return;
      if (!Number.isFinite(g.targetPence) && Number.isFinite(g.targetTokens)) g.targetPence = tokensToPence(g.targetTokens, goalRate);
      if (Number.isFinite(g.targetPence)) delete g.targetTokens;
    });
    if (!AVATARS.includes(p.avatar)) p.avatar = OLD_AVATAR_MAP[p.avatar] || AVATARS[0];
    // Savings Vault balance, in pence — separate from the token balance. Older saved
    // profiles predate this field. (Renamed from bankBalancePence, Aug 2026: the app has no
    // bank-account concept any more, so the name described something that doesn't exist.)
    if (p.vaultBalancePence === undefined) p.vaultBalancePence = (p.bankBalancePence || 0);
    delete p.bankBalancePence;
    // Vault interest opt-out, per child (Alan, Aug 2026). The Savings Vault itself is no
    // longer optional, so the old per-child vault toggle (hasBankAccount) is gone — but it
    // WAS doubling as the interest opt-out, so that capability has to survive the change or
    // a household that deliberately excluded one child would silently start paying them.
    // Hence the derivation: a child whose vault was switched off does not begin earning.
    if (p.earnsVaultInterest === undefined) p.earnsVaultInterest = p.hasBankAccount !== false;
    delete p.hasBankAccount;
    // Vault Interest bookkeeping — the last Monday interest was settled up to (null until
    // interest is first enabled; applyVaultInterest self-heals a missing anchor to "now"
    // rather than paying history), plus the sub-penny carry (fractional pence).
    if (p.vaultInterestAnchor === undefined) p.vaultInterestAnchor = null;
    if (typeof p.vaultInterestCarry !== "number") p.vaultInterestCarry = 0;
    // Daily-habit streak settings — how many complete days per week earns the reward, and
    // how many tokens that reward is worth. Every child always has an explicit value (see
    // settings.applyHabitToAll below for how AdjustTab keeps these in sync across kids).
    if (p.habitThreshold === undefined) p.habitThreshold = 7;
    // W4 streak repair: which week (weekStart string) this child's one-per-week repair
    // was last used in. Comparing against the CURRENT weekStart is the whole allowance
    // mechanic — no counters to reset, a new week simply stops matching.
    if (p.lastStreakRepairWeek === undefined) p.lastStreakRepairWeek = null;
    if (p.habitReward === undefined) p.habitReward = 20;
    // Weekly spending cap — on by default at £5/week for every child, existing and new,
    // per the 2026-08-15 scoping decision. A parent can turn it off or change the amount
    // per child from the Rewards tab immediately after this deploys.
    if (!p.spendingCap || typeof p.spendingCap !== "object") p.spendingCap = { enabled: true, weeklyGBP: DEFAULT_SPENDING_CAP_GBP };
    if (p.spendingCap.enabled === undefined) p.spendingCap.enabled = true;
    if (p.spendingCap.weeklyGBP === undefined) p.spendingCap.weeklyGBP = DEFAULT_SPENDING_CAP_GBP;
    // Oinks (fun-only currency, see lib/oinks.js) — balance plus owned/equipped cosmetics.
    // Never money: deliberately NOT mirrored into transactions, exchange rates, or the
    // spending cap. Older profiles predate all three fields.
    if (typeof p.oinks !== "number") p.oinks = 0;
    if (!p.cosmetics || typeof p.cosmetics !== "object") p.cosmetics = {};
    if (!Array.isArray(p.cosmetics.owned)) p.cosmetics.owned = [];
    if (!p.cosmetics.equipped || typeof p.cosmetics.equipped !== "object") p.cosmetics.equipped = { hat: null, frame: null, celebration: null };
    // Trophy Cabinet badges ({ id, earnedAt }) — stored rather than derived, so pruned
    // history can never un-earn one (see lib/badges.js).
    if (!Array.isArray(p.badges)) p.badges = [];
  });
  if (!Array.isArray(parsed.transactions)) parsed.transactions = [];
  if (!Array.isArray(parsed.activities)) parsed.activities = [];
  if (!Array.isArray(parsed.experienceRewards)) parsed.experienceRewards = [];
  if (!Array.isArray(parsed.habits)) parsed.habits = [];
  if (!Array.isArray(parsed.habitLog)) parsed.habitLog = [];
  if (!Array.isArray(parsed.requests)) parsed.requests = [];
  // Requests/transactions created before timestamps existed have no timestamp field — backfill so the
  // UI never shows a blank time instead of silently hiding it.
  const fallbackTime = (parsed._meta && parsed._meta.lastModified) || new Date(0).toISOString();
  parsed.transactions.forEach((t) => {
    if (!t.timestamp) t.timestamp = fallbackTime;
    // Transactions predate the token/vault balance split — all older entries affected tokens.
    if (!t.balance) t.balance = "token";
    // "bank" -> "vault" (Alan, Aug 2026). t.balance is PERSISTED on every transaction row and
    // drives amount formatting, transfer pairing and redemption accounting, so the rename is
    // a data migration rather than a find-and-replace.
    //
    // This is PERMANENT, not a one-shot for the release: any device still holding un-synced
    // local state written by the old build will keep producing "bank" rows after the new
    // build ships, and there's no window after which that becomes impossible. Cheap to keep;
    // silently corrupting to remove.
    if (t.balance === "bank") t.balance = "vault";
  });
  parsed.requests.forEach((r) => {
    if (!r.timestamp) r.timestamp = fallbackTime;
    if (r.status !== "pending" && !r.resolvedAt) r.resolvedAt = r.timestamp;
    // requestType "bank_transfer" -> "vault_transfer" (Alan, Aug 2026), same reasoning and
    // same permanence as the transaction t.balance migration above: it's persisted, it's
    // matched on by both the approval flow and the row renderer, and an un-synced device can
    // still produce the old value after this ships.
    if (r.requestType === "bank_transfer") r.requestType = "vault_transfer";
  });
  if (!parsed.learningContent || typeof parsed.learningContent !== "object") parsed.learningContent = {};
  parsed.profiles.forEach((p) => {
    if (!parsed.learningContent[p.id]) parsed.learningContent[p.id] = { spellingWords: [], vocabBank: [] };
    const lc = parsed.learningContent[p.id];
    if (!Array.isArray(lc.spellingWords)) lc.spellingWords = [];
    if (!Array.isArray(lc.vocabBank)) lc.vocabBank = [];
    // LB-366: the test history (src/lib/testHistory.js). Starts empty; there is nothing to
    // backfill from, because nothing before this release recorded the questions.
    if (!lc.testHistory || typeof lc.testHistory !== "object" || Array.isArray(lc.testHistory)) lc.testHistory = {};
    // LB-364: homework practice lists (src/lib/homework.js). Starts empty.
    if (!lc.homework || typeof lc.homework !== "object" || Array.isArray(lc.homework)) lc.homework = { lists: [] };
    if (!Array.isArray(lc.homework.lists)) lc.homework.lists = [];
    // Older saved words predate per-word test selection — default them to included.
    lc.spellingWords.forEach((w) => { if (w.testInclude === undefined) w.testInclude = true; });
    // Words mastered before spaced repetition (R3b) existed have no masteredAt — start
    // their refresher clock from upgrade day rather than flooding day-one refreshers.
    lc.vocabBank.forEach((v) => { if ((v.correctCount || 0) >= 2 && !v.masteredAt) v.masteredAt = todayStr(); });
    // Reward/test settings used to be one global config shared by every child — now each
    // child has their own. Households upgrading from the old shape had these under
    // parsed.settings; carry that value forward as this child's starting point (rather than
    // silently resetting everyone to the hardcoded default) so behavior doesn't change on
    // upgrade, then a parent can diverge per child from there.
    if (lc.spellingReward === undefined) lc.spellingReward = parsed.settings.spellingReward ?? 5;
    if (lc.vocabPassReward === undefined) lc.vocabPassReward = parsed.settings.vocabPassReward ?? 3;
    if (lc.vocabFullReward === undefined) lc.vocabFullReward = parsed.settings.vocabFullReward ?? 5;
    if (lc.vocabTestSize === undefined) lc.vocabTestSize = parsed.settings.vocabTestSize ?? VOCAB_TEST_SIZE;
    // LB-19 §7: spelling mirrors vocab — a pass tier at 80%. Existing households are
    // backfilled at the same 3:5 ratio of their full-marks reward and told once (the row line).
    if (lc.spellingPassReward === undefined) { lc.spellingPassReward = Math.max(1, Math.round(((lc.spellingReward ?? 5) * 3) / 5)); lc.spellingPassNotice = true; }
    // LB-283 retro repair (Alan, 15 Sep). The forward fix stops spellingPassReward drifting,
    // but it cannot mend a value already written against an old exchange rate:
    // rescaleHouseholdRewards omitted this one key, so for any household that changed their
    // rate, spelling's pass reward alone kept its old token count while every other reward
    // moved with it.
    //
    // Repaired ONLY where the app itself wrote the value and the parent has not since changed
    // it. `_seededRewards` is the existing record of what the app wrote — the same mechanism
    // stampSeededRewards uses to leave a parent's own numbers alone on a re-seed. A blanket
    // recompute would overwrite a deliberate choice, and a parent who had set spelling's pass
    // reward by hand would have no idea why it moved.
    //
    // One-shot and marked: without the marker, a parent who later sets it back to the "wrong"
    // value on purpose would have it corrected away from them again on the next load.
    else if (!lc._spellingPassRepaired) {
      const seeded = lc._seededRewards || {};
      const expected = Math.max(1, Math.round(((lc.spellingReward ?? 5) * 3) / 5));
      const appWroteIt = seeded.spellingReward !== undefined && lc.spellingReward === seeded.spellingReward;
      // The marker is written ONLY when something was actually changed. The first version set
      // it unconditionally, which burned the one shot on households the check could not decide
      // — including, before the fix above, every household that had ever changed its exchange
      // rate, which is the exact population the repair exists for. Leaving it unwritten costs
      // one comparison per load and keeps a better-informed repair possible later.
      if (appWroteIt && lc.spellingPassReward !== expected) {
        lc.spellingPassReward = expected;
        lc._spellingPassRepaired = true;
      }
    }
    if (!lc.adaptive || typeof lc.adaptive !== "object") lc.adaptive = {};
    if (lc.vocabPassThreshold === undefined) lc.vocabPassThreshold = parsed.settings.vocabPassThreshold ?? DEFAULT_PASS_THRESHOLD;
    // Spelling's pass mark, now a per-child setting like vocab's and maths' (Alan, 22 Sep:
    // spelling pays like the other games). 75 is what the test has always paid at, so no
    // household's payouts change on upgrade. A percentage, so it deliberately does NOT go
    // through seed()'s exchange-rate scaling.
    if (lc.spellingPassThreshold === undefined) lc.spellingPassThreshold = DEFAULT_PASS_THRESHOLD;
    if (lc.learnEarnPayoutsPerWeek === undefined) {
      // Migrate the old "cooldown in days" model to "payouts per week": a 7-day (or longer)
      // cooldown behaved like 1 payout/week, so that's the closest equivalent default. Any
      // stale per-child cooldown value (from before this migration ran) is dropped either way.
      lc.learnEarnPayoutsPerWeek = 1;
    }
    delete lc.learnEarnCooldownDays;
    // Maths games (TT Streaks, Maths Workout) share their own weekly payout cap, kept
    // separate from the English one above so a household can pace the two subjects
    // independently rather than one shared number silently governing both.
    if (lc.mathsPayoutsPerWeek === undefined) lc.mathsPayoutsPerWeek = 1;
    if (!Array.isArray(lc.ttTables) || lc.ttTables.length === 0) lc.ttTables = [2, 3, 4, 5, 10];
    if (lc.ttSessionSize === undefined) lc.ttSessionSize = 20;
    if (lc.ttStreakPassCount === undefined) lc.ttStreakPassCount = Math.round(lc.ttSessionSize * 0.8);
    if (lc.ttStreakPassReward === undefined) lc.ttStreakPassReward = 3;
    if (lc.ttStreakFullReward === undefined) lc.ttStreakFullReward = 5;
    if (lc.mathsBand === undefined) lc.mathsBand = "auto";
    if (lc.mathsTestSize === undefined) lc.mathsTestSize = 12;
    // LB-280: words per Coach test, overridable per child.
    if (lc.coachTestSize === undefined) lc.coachTestSize = DEFAULT_COACH_TEST_SIZE;
    if (!lc.coachWords || typeof lc.coachWords !== "object") lc.coachWords = {};
    if (lc.mathsPassThreshold === undefined) lc.mathsPassThreshold = DEFAULT_PASS_THRESHOLD;
    // Alan, 13 Sep: the pass mark drops from 80% to 75% wherever a game scores by percentage.
    // Moving the default alone would change nothing for anyone already using the app, because
    // the migration that created these fields wrote 80 in explicitly rather than leaving them
    // undefined. So shift a stored 80 once, and record that it has run: without the marker, a
    // parent who deliberately set 80 back would have it taken off them again on the next load.
    // Times Tables is untouched — it passes on a streak count, not a percentage.
    if (!lc.passMark75) {
      if (lc.vocabPassThreshold === 80) lc.vocabPassThreshold = DEFAULT_PASS_THRESHOLD;
      if (lc.mathsPassThreshold === 80) lc.mathsPassThreshold = DEFAULT_PASS_THRESHOLD;
      lc.passMark75 = true;
    }
    if (lc.mathsPassReward === undefined) lc.mathsPassReward = 3;
    if (lc.mathsFullReward === undefined) lc.mathsFullReward = 5;
    if (lc.mathsTimerSeconds === undefined) lc.mathsTimerSeconds = 0;
    // Per-game visibility — every game defaults to visible so existing households see no
    // change until a parent actively hides one from Parent Mode → Learn & Earn.
    if (lc.spellingEnabled === undefined) lc.spellingEnabled = true;
    if (lc.vocabEnabled === undefined) lc.vocabEnabled = true;
    if (lc.ttstreaksEnabled === undefined) lc.ttstreaksEnabled = true;
    if (lc.mathsworkoutEnabled === undefined) lc.mathsworkoutEnabled = true;
    // R5 — Piggington's Arcade (Snack Attack) settings: visible by default (a zero-setup
    // feature whose absence is what a parent would notice), Brain Warm-Up required by
    // default. Same "hide without losing settings" semantics as the per-game flags above.
    if (!lc.arcade || typeof lc.arcade !== "object") lc.arcade = {};
    if (lc.arcade.snackCatchEnabled === undefined) lc.arcade.snackCatchEnabled = true;
    if (lc.arcade.warmupMode === undefined) lc.arcade.warmupMode = "warmup";
  });
  // These now live per-child (above) — drop the old global copies so they don't linger as
  // dead, confusing state alongside the per-child values.
  delete parsed.settings.spellingReward;
  delete parsed.settings.vocabPassReward;
  delete parsed.settings.vocabFullReward;
  delete parsed.settings.vocabTestSize;
  delete parsed.settings.vocabPassThreshold;
  delete parsed.settings.learnEarnCooldownDays;
  if (!Array.isArray(parsed.pendingWordbankSubmissions)) parsed.pendingWordbankSubmissions = [];
  // Alan, 29 Sep (prod): words already on a list never reached the word bank, because a word is
  // only sent when it is added, and homework lists made before that change were never sent at
  // all. Once per household, everything the app would send today goes into the queue, and the
  // flush in App.jsx sends it; the server voices each clean word, including custom words that
  // were sitting unapproved from before auto-approval.
  if (parsed.wordbankBackfill !== 1) {
    const have = new Set(parsed.pendingWordbankSubmissions.map((w) => String(w).trim()));
    for (const w of wordbankBackfillWords(parsed)) if (!have.has(w)) { have.add(w); parsed.pendingWordbankSubmissions.push(w); }
    parsed.wordbankBackfill = 1;
  }
  // capMode records which question the parent answered on the wizard's token step: one cap
  // for the household, or a cap each. Derived for existing households from the shape of the
  // data itself rather than asked again — if every child already sits on the same cap, the
  // household has been behaving as "global" whether or not anyone ever chose it.
  // LB-370: when this household first shared its referral link (ISO), or null. Retires the
  // parent Home card; Settings → Household keeps the row regardless.
  if (parsed.settings.referralSharedAt === undefined) parsed.settings.referralSharedAt = null;
  if (parsed.settings.capMode !== "global" && parsed.settings.capMode !== "perChild") {
    const caps = (parsed.profiles || []).map((p) => p.spendingCap?.weeklyGBP);
    parsed.settings.capMode = caps.length > 1 && caps.some((c) => c !== caps[0]) ? "perChild" : "global";
  }
  migrateGlobalActivitiesToPerChild(parsed);
  if (!parsed._meta) parsed._meta = { revision: 0, lastModified: null };
  return parsed;
}

/* --------------------- global activities -> per-child tasks ----------------------
 * Activities used to be household-global by default (childId: null), which made them
 * impossible to price against a cap: one shared token value across children whose caps
 * may differ by 3x means it is right for nobody. Every activity now belongs to exactly
 * one child.
 *
 * Two rules make this migration safe to run over live households:
 *
 *  1. VALUES ARE NEVER REWRITTEN. Each child's copy keeps the original token value, so
 *     nothing a kid can earn changes on the day this deploys. Repricing them to each
 *     child's cap is a thing a parent chooses to do (Reprice / the review page), never
 *     something a migration does behind their back.
 *
 *  2. THE COPIES ARE LINKED. They share one linkKey, so the review page can tell "these
 *     deliberately differ because their caps differ" apart from "one was edited and the
 *     other forgotten". Note the deliberate consequence: a freshly migrated set is
 *     UNIFORM in tokens across possibly-different caps, which is exactly the shape the
 *     share-based drift check would otherwise flag on every row on day one. That is why
 *     linkedDrift() requires the VALUES to differ before it looks at shares at all.
 *
 * Idempotent by construction: it only ever looks at childId-less rows, and removes every
 * one it processes. A household with no children keeps its globals untouched rather than
 * dropping them on the floor — they get split the moment a child exists.
 */
export function migrateGlobalActivitiesToPerChild(parsed) {
  const globals = (parsed.activities || []).filter((a) => !a.childId);
  if (!globals.length) return false;
  const children = parsed.profiles || [];
  if (!children.length) return false;
  const perChild = [];
  globals.forEach((g) => {
    const linkKey = g.linkKey || uid();
    children.forEach((c) => {
      perChild.push({ ...g, id: uid(), childId: c.id, linkKey });
    });
  });
  parsed.activities = (parsed.activities || []).filter((a) => a.childId).concat(perChild);
  return true;
}

/* -------------------------------- avatar ---------------------------------- */

// A real, in-flow block of empty space at the top of every top-level screen — deliberately
// NOT relying solely on `body { padding-top: env(safe-area-inset-top) }`, because that env()
// value only ever comes out non-zero under fairly specific conditions (installed to the home
// screen, standalone display mode, iOS's own quirks around when it considers the page
// "edge-to-edge") and was reported not showing up at all on a real iPhone. A fixed-height
// spacer always reserves real space regardless of whether the browser's safe-area env()
// resolves to anything; it also adds a bit more on top for devices that DO report a real
// inset (notch/Dynamic Island), so the two mechanisms stack rather than fight each other.

export const OLD_AVATAR_MAP = { robot: "🤖", rocket: "🚀", cat: "🐱", dog: "🐶", ghost: "👻", star: "⭐", zap: "⚡", crown: "👑" };

// W2-PR(iii): append one settings-audit entry, newest first, capped at 100. `what` is a
// stable machine key (e.g. "exchangeRate", "vaultInterest.apr", "parentPin"); from/to
// are display-ready strings — the PIN site passes "••••••" for both, the log records
// THAT it changed, never the value.
// W4 — rate-change marker row (workplan: "visibility + regression test"). A zero-amount,
// childless feed entry so "the token value changed here" is visible in the household's
// Recent activity right where balances would appear to jump. NEVER touches balances:
// balances are stored fields adjusted only by their own handlers, and every consumer
// that money-sums filters by childId/type — this row has childId null and its own type.
export function rateMarkerTxn(deviceId, fromRate, toRate) {
  return { id: uid(), deviceId: deviceId || null, childId: null, amount: 0, reason: `Token value changed: ${fromRate} = £1 → ${toRate} = £1`, type: "rate_marker", balance: "token", timestamp: nowIso(), rateMarker: true };
}

// LB-343: `acting` is { id, name } for the adult doing this — see actingAdult in
// src/lib/adults.js. G2 names the adult on a settings change ("Changed the exchange rate /
// Jo · 10p to 12p a token"), and this log is what that row is drawn from, so it carries the
// adult like every other write now does. Optional on purpose: a caller with nobody to name
// writes the row unattributed rather than not writing it.
export function logSetting(s, deviceId, what, from, to, acting = null) {
  if (!Array.isArray(s.settingsLog)) s.settingsLog = [];
  s.settingsLog.unshift({
    at: new Date().toISOString(), deviceId: deviceId || null, what, from: String(from), to: String(to),
    ...(acting && acting.id ? { adultId: acting.id } : {}),
    ...(acting && acting.name ? { adultName: acting.name } : {}),
  });
  if (s.settingsLog.length > 100) s.settingsLog.length = 100;
  return s;
}
